define([
	"dojo/_base/declare",
	"dojo/aspect",
	"dojo/_base/lang",
	"dojo/when",
	"lodash",
	"csi",
	"csi/action/Switchboard",
	"csi/_base/Deferred",
	"csi/config/preferences",
	"csi/constants",
	"csi/core/Key",
	"csi/heading/_base",
	"csi/heading/Spec",
	"csi/locale",
	"csi/reflection",
	"csi/store/_base",
	"csi/store/Names",
	"csi/store/Nodes",
	"csi/table/matrix/Path",
	"csi/utility/pathUtility",
	"csi/view/ToolbarPluginSwitchboard",
	"WBS/WbsDate",
	"WBS/StateColorRenderer",
	"csi/xml",
	"csi/xml/Search",
], function(declare, aspect, lang, when, _, csi, Switchboard, Deferred, configPreferences, {CSS_TABLE_COLUMNS, FMT_ATTR_LIST}, Key, csiHeading, 
		Spec, locale, reflection, csiStore, Names, Nodes, Path, {pathCreate}, ToolbarPluginSwitchboard, WbsDate, StateColorRenderer, xml, Search) {

	const WBS_ELEMENT = "WbsElement";
	const WBS_OPTION = "wbsMileStoneTemplateURLFromHierarchyViews";
	const WBS_SETTINGS_URL = "centric://REFLECTION/INSTANCE/WbsConfig/Settings";

	const WBS_MATRIX_KEY = new Key({
		id: "WBS",
	});

	const _SwitchboardPlugin = declare(ToolbarPluginSwitchboard, {
		disableButtonAction: true,
		idViewProperty: WBS_OPTION,
		placeHolder: "SelectOne",
		postAction: "postActionRefresh",
		hideGo: true,

		/**
		 * @override
		 * Hide the plugin if the store has less than 2 templates (3 including empty value) or WBS matrix is not in the view
		 */
		canShowPlugin() {
			return (this.store?.size() > 2) && this.getView().isUntypedKeyInView(WBS_MATRIX_KEY);
		},

		/**
		 * @override
		 * Nothing to do when using idViewProperty
		 */
		onChangeItem(view, node) { },

		/**
		 * @override
		 * Hide if printing
		 */
		validatePlugin(ctx) {
			return !ctx.bPrint;
		},

		/**
		 * @override
		 * If not showing the plugin, then no reason to update the view property - doing so can
		 * cause the custom view to be dirty when switching custom views if that custom view
		 * references a template that is no longer a valid template
		 */
		_onWatchViewProperty(attr, oldVal, newVal) {
			if (this.canShowPlugin()) {
				this.inherited(arguments);
			}
		}
	});

	const _elementsByNameRenderer = declare(StateColorRenderer, {
		/**
		 * allow using path for the matrix
		 * @override
		 */
		pushContext() {
			return true;
		},

		/**
		 * Don't render cell if it does not have corresponding Milestone Task
		 * @override
		 */
		headingToHtml: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode) {
			if (ctx.getNodeType() !== "WbsElement") {
				return "";
			}
			return this.inherited(arguments);
		},
	})

	return declare(Path, {
		bAllowColumnFilter: true,
		hideLoneSubheader: true,

		/**
		 * Add deferred query for milestone elements
		 */
		addDeferred(ctx, h, dfds) {
			const {view} = ctx;
			const {wbsMilestoneTemplatesStore, wbsMileStoneTemplateURLFromHierarchyViews} = view;

			if (!wbsMilestoneTemplatesStore) {
				return;
			}

			const dfd = new Deferred();

			dfds.push(dfd);

			when(wbsMilestoneTemplatesStore.query(), async (results) => {
				// see if existing template value is still a template
				let url = wbsMileStoneTemplateURLFromHierarchyViews;
				let template = wbsMilestoneTemplatesStore.get(url);
				if (!template || !xml.isValidURL(url)) {
					// try default value (if only one value)
					url = await wbsMilestoneTemplatesStore.getDefaultValue();
					template = wbsMilestoneTemplatesStore.get(url);
				}

				if (!template) {
					url = csi.EMPTY_URL;
				} else if (!view.wbsElementsStore || (url !== view.wbsPreviousTemplateUrl)) {
					// create store if template is different from before
					view.wbsPreviousTemplateUrl = url;
					view.wbsElementsStore = new Nodes({
						typeNode: WBS_ELEMENT,
						resultUrls: xml.isValidURL(url) ? undefined : [],
						requestParameters: {
							"Qry.Limit.Path": xml.getUrlPath(url, pathCreate("Elements"))
						}
					});
				}

				// NOTE: If we want the custom view to be updated with the template being displayed,
				// then uncomment the line below and remove _onWatchViewProperty override in plugin.
				// We may want the custom view to be updated so that any server use of the custom view
				// is (more) correct. The only way to guarantee the custom view is correct is to update
				// all custom views as Update Configuration is run after changing the milestone templates
				// and their applicability
				// ctx.view.set(WBS_OPTION, url);

				when(view.wbsElementsStore.query(), () => {
					dfd.resolve();
				});
			});
		},

		/**
		 * @override
		 * Return store for custom view filtering
		 */
		getColumnQueryLevelStore(ctx, h) {
			return new Nodes({
				typeNode: WBS_ELEMENT,
				requestParameters: {
					"Qry.Limit.Path": xml.getUrlPath(WBS_SETTINGS_URL, pathCreate("MilestoneTemplates", "Elements"))
				}
			});
		},

		/**
		 * @override
		 * Return column specs for 1d or composite size range
		 */
		getColumnSpecs: function(ctx, h) {
			const view = ctx.view;
			const nodes = view.wbsElementsStore?.query() || [];
			const specs = this.inherited(arguments);
			const rootSpec = specs[0];
			rootSpec.headerParams.colSpan += nodes?.length || 0;
			const subs = rootSpec?.subs;
			if (!subs) {
				return specs;
			}
			nodes.forEach(node => {
				const name = node.$Name;
				const heading = _.extend(view.defView.newHeading(), {
					m_bAugmentable: true,
					m_name: name,
					m_id: "ScheduleStatus",
					m_path: "Child:ElementsByName/Index:" + name,
					m_right: "None",
					classRenderer: _elementsByNameRenderer,
				});
				[
					{id:"PlannedEnd", separator:locale.resource("WbsMilestoneSeparatorPlanned")}, 
					{id:"ExpectedEnd", separator:locale.resource("WbsMilestoneSeparatorEstimated")}, 
					{id:"ActualEnd", separator:locale.resource("WbsMilestoneSeparatorActual")}
				].forEach(item=>{
					heading.addSubHeading(
						_.extend(view.defView.newHeading(), {
							m_id: item.id,
							m_right: "None",
							m_separator: item.separator,
							classRenderer: WbsDate,
						}), true )
				});
				rootSpec.heading.addSubHeading(heading, true);
				const spec = new Spec({
					headerParams: {
						className: CSS_TABLE_COLUMNS,
						innerText:name,
					},
					heading,
					customMatrixStandardPath: "Child:WBS/" + heading.m_path,
				});
				spec.querySpec = csiHeading.getQuerySpec(ctx, spec);
				spec.querySpec.pathInMatrix = "Child:ElementsByName/Index:" + name;
				spec.queryKey = xml.queryKey("ScheduleStatus", null, spec.querySpec.path);
				spec.name = name;

				subs.push(spec);
			});
			return specs;
		},

		/**
		 * @override
		 * Add custom view option to view, as well as switchboard plugin
		 */
		initializeView(view) {
			// create milestone store for target type of view
			const rc = view.defView.getPageClass();
			const search = new Search();
			search.setLimitPath(WBS_SETTINGS_URL, pathCreate("MilestoneTemplates"));

			if (!rc.abstract) {
				search.getQuery().Or()
					.Attr("ApplicableForTypes", xml.OP_NULL)
					.Attr("ApplicableForTypes", xml.OP_EQUALS, "ApplicableForTypes:" + rc.type);
			}

			const store = view.wbsMilestoneTemplatesStore = new Names({
				data: [csiStore.nameObject("")],
				request: search.getRequest(),
			});

			// add switchboard to toolbar
			const action = Object.assign(new Switchboard(), {
				bSecondaryToolbar: true,
				classToolbarPlugin: _SwitchboardPlugin,
				classToolbarPluginParams: {store},
				typeNode: "Wbs",
				m_id: "MilestoneTemplate",
			});
			action.resolve();
			view.plugins.push(action);

			// add custom view option
			view.addAdHocCustomViewOption(WBS_OPTION, csi.EMPTY_URL);

			// query optimization
			if (!configPreferences.getUserDefaultsForType(WBS_ELEMENT)) {
				view.own(aspect.after(view, "onCreateMainQueryLevel", (ql) => this._optimizeViewQuery(ql), true));
			}
		},

		/**
		 * @override
		 * Disable matrix if not enabled for the target class of the view
		 */
		isValidForClass(targetClass) {
			const rca = reflection.getClassAttribute(targetClass, "WbsEnabled");
			return rca?.initialValue || (targetClass.type === "WbsHolder");
		},

		/**
		 * Optimize query so that only certain attributes are returned for WbsElement nodes
		 * @param {QueryLevel} ql
		 */
		_optimizeViewQuery(ql) {
			const a = ql.requestParameters?.[FMT_ATTR_LIST] || [];
			a.push(`WbsElement:ScheduleStatus;PlannedEnd;ExpectedEnd;ActualEnd;WBS;`);
			ql.requestParameters[FMT_ATTR_LIST] = a;
		},	
	});
});